/**
 * Personal API tokens — named, revocable, expiring tokens a user mints for
 * scripts, CI and the CLI (`Authorization: Bearer <token>`, or
 * `MOLECULE_TOKEN` for mlcl). Mounted by the settings panel, beside the
 * account Team section, following the same conventions.
 *
 * Consumes:
 *   - `GET    /account/api-tokens`      → the list, newest first
 *   - `POST   /account/api-tokens`      → `{ name, expiresInDays }`; the token comes back ONCE
 *   - `DELETE /account/api-tokens/:id`  → revoke
 *
 * The raw token lives only in this component's state, only until the user
 * dismisses the reveal, collapses the section, or leaves the panel. It is
 * never logged, persisted, or sent anywhere but the clipboard on request.
 *
 * Errors read the server's own sentence (`lib/server-error.ts`): the HTTP
 * client's `HttpError.errorKey` is always its generic key, never the server's.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Input, Select } from '@molecule/app-ui-react'

import { useIsMobile } from '../hooks/useMediaQuery.js'
import { copyToClipboard } from '../lib/code-copy.js'
import { serverErrorKey, serverErrorMessage } from '../lib/server-error.js'
import { confirmDialog } from './AppDialogs.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'

/** One token as `GET /account/api-tokens` lists it (no secret). */
export interface ApiToken {
  id: string
  name: string
  createdAt: string
  expiresAt: string
  lastUsedAt: string | null
}

/** What `POST /account/api-tokens` answers: the row plus the one-time token. */
interface CreatedApiToken extends ApiToken {
  token: string
}

/** The most active tokens one account may hold (the API enforces it). */
export const MAX_ACTIVE_API_TOKENS = 25

/** Longest name the API accepts. */
export const API_TOKEN_NAME_MAX = 60

/** Expiry choices offered, in days. 90 is the default (and the API's). */
export const API_TOKEN_EXPIRY_CHOICES = [30, 90, 180, 365] as const

const DEFAULT_EXPIRY_DAYS = 90

/** How long the "Copied" label shows. */
const COPIED_MS = 2000

type Translate = ReturnType<typeof useTranslation>['t']

/**
 * The sentence to show for a failed token request. Two refusals get our own
 * plain wording because the fix is somewhere else (a signed-in session, a
 * confirmed email); everything else shows what the server said.
 *
 * @param error - The thrown request error.
 * @param t - The translate function.
 * @param fallback - The line to show when the server sent no sentence.
 * @returns The message to render.
 */
export function apiTokenErrorMessage(error: unknown, t: Translate, fallback: string): string {
  const key = serverErrorKey(error)
  if (key === 'apiToken.error.sessionRequired') {
    return t('apiTokens.sessionRequired', undefined, {
      defaultValue: 'Managing tokens needs a signed-in session, not an API token.',
    })
  }
  if (key === 'apiToken.error.emailNotConfirmed') {
    return t('apiTokens.emailNotConfirmed', undefined, {
      defaultValue: 'Confirm your email address first, then create a token.',
    })
  }
  return serverErrorMessage(error) ?? fallback
}

/**
 * A short local date, or the raw value when it does not parse.
 *
 * @param iso - An ISO timestamp.
 * @returns The formatted date.
 */
function shortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Whether a token's expiry has passed.
 *
 * @param token - The token row.
 * @returns True once `expiresAt` is in the past.
 */
function isExpired(token: ApiToken): boolean {
  const at = new Date(token.expiresAt).getTime()
  return !Number.isNaN(at) && at <= Date.now()
}

/**
 * The one-time reveal of a freshly created token: read-only monospace field,
 * Copy, a one-line warning, and Hide (which drops the token from state).
 *
 * @param props - The reveal props.
 * @param props.created - The new token, with its secret.
 * @param props.onDismiss - Clears the reveal.
 * @returns The reveal block.
 */
function TokenReveal({
  created,
  onDismiss,
}: {
  created: { name: string; token: string }
  onDismiss: () => void
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const copy = useCallback(async () => {
    const ok = await copyToClipboard(created.token)
    setCopyState(ok ? 'copied' : 'failed')
    window.clearTimeout(timer.current)
    if (ok) timer.current = window.setTimeout(() => setCopyState('idle'), COPIED_MS)
  }, [created.token])

  return (
    <div
      data-mol-id="api-tokens-reveal"
      className={cm.cn(cm.alert({ variant: 'warning' }), cm.stack(2), cm.sp('mb', 3))}
    >
      <label htmlFor="api-tokens-reveal-token" className={cm.formLabelSmall}>
        {t(
          'apiTokens.reveal.label',
          { name: created.name },
          { defaultValue: 'Your new token: {{name}}' },
        )}
      </label>
      <Flex align="center" gap="xs" style={{ flexWrap: 'wrap' }}>
        <Input
          id="api-tokens-reveal-token"
          data-mol-id="api-tokens-reveal-token"
          readOnly
          value={created.token}
          size="sm"
          align="left"
          onFocus={(e) => (e.target as HTMLInputElement).select()}
          // Monospace: a token is read character by character. 16px on
          // phones so iOS does not zoom the page when the field is focused.
          style={{ flex: '1 1 220px', fontFamily: 'monospace', fontSize: isMobile ? 16 : 12 }}
        />
        <Button
          size="sm"
          className={cm.touchTargetCompact}
          data-mol-id="api-tokens-reveal-copy"
          onClick={() => void copy()}
        >
          {copyState === 'copied'
            ? t('apiTokens.reveal.copied', undefined, { defaultValue: 'Token copied' })
            : t('apiTokens.reveal.copy', undefined, { defaultValue: 'Copy token' })}
        </Button>
      </Flex>
      <p className={cm.textSize('sm')} data-mol-id="api-tokens-reveal-warning">
        {t('apiTokens.reveal.warning', undefined, {
          defaultValue: 'Copy it now. It is shown only once.',
        })}
      </p>
      {copyState === 'failed' && (
        <p className={cm.cn(cm.textSize('xs'), cm.textError)} data-mol-id="api-tokens-copy-failed">
          {t('apiTokens.reveal.copyFailed', undefined, {
            defaultValue: 'Could not copy. Select the token and copy it yourself.',
          })}
        </p>
      )}
      <div>
        <Button
          size="sm"
          variant="outline"
          className={cm.touchTargetCompact}
          data-mol-id="api-tokens-reveal-dismiss"
          onClick={onDismiss}
        >
          {t('apiTokens.reveal.dismiss', undefined, { defaultValue: 'Hide token' })}
        </Button>
      </div>
    </div>
  )
}

/**
 * Account-level personal API tokens: list, create, one-time reveal, revoke.
 *
 * @param props - The section props.
 * @param props.open - Whether the surrounding card is expanded. Collapsing it
 *   clears any revealed token, the same as leaving the panel.
 * @returns The section body.
 */
export function AccountApiTokensSection({ open = true }: { open?: boolean }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const isMobile = useIsMobile()

  const [tokens, setTokens] = useState<ApiToken[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [expiry, setExpiry] = useState(String(DEFAULT_EXPIRY_DAYS))
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [revealed, setRevealed] = useState<{ name: string; token: string } | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await http.get<{ data: ApiToken[] }>('/account/api-tokens')
      setTokens(Array.isArray(res.data?.data) ? res.data.data : [])
      setLoadError(null)
    } catch (error) {
      setLoadError(
        apiTokenErrorMessage(
          error,
          t,
          t('apiTokens.loadFailed', undefined, { defaultValue: 'Could not load your tokens.' }),
        ),
      )
    }
    setLoading(false)
  }, [http, t])

  useEffect(() => {
    void load()
  }, [load])

  // Collapsing the card is leaving it: the secret goes with it.
  useEffect(() => {
    if (!open) setRevealed(null)
  }, [open])

  const activeCount = (tokens ?? []).filter((token) => !isExpired(token)).length
  const atLimit = activeCount >= MAX_ACTIVE_API_TOKENS
  const trimmedName = name.trim()

  const create = useCallback(async () => {
    if (!trimmedName) return
    setCreating(true)
    setCreateError(null)
    setRevealed(null)
    try {
      const res = await http.post<{ data: CreatedApiToken }>('/account/api-tokens', {
        name: trimmedName,
        expiresInDays: Number(expiry),
      })
      const created = res.data?.data
      if (created?.token) setRevealed({ name: created.name, token: created.token })
      setName('')
      await load()
    } catch (error) {
      setCreateError(
        apiTokenErrorMessage(
          error,
          t,
          t('apiTokens.createFailed', undefined, { defaultValue: 'Could not create the token.' }),
        ),
      )
    }
    setCreating(false)
  }, [expiry, http, load, t, trimmedName])

  const revoke = useCallback(
    async (token: ApiToken) => {
      const confirmed = await confirmDialog({
        message: t(
          'apiTokens.revokeConfirm',
          { name: token.name },
          { defaultValue: 'Revoke "{{name}}"? Anything using it will stop working.' },
        ),
        confirmLabel: t('apiTokens.revoke', undefined, { defaultValue: 'Revoke' }),
        danger: true,
        molId: 'api-tokens-revoke-dialog',
      })
      if (!confirmed) return
      setRevokingId(token.id)
      setRevokeError(null)
      try {
        await http.delete(`/account/api-tokens/${encodeURIComponent(token.id)}`)
        await load()
      } catch (error) {
        setRevokeError(
          apiTokenErrorMessage(
            error,
            t,
            t('apiTokens.revokeFailed', undefined, {
              defaultValue: 'Could not revoke that token.',
            }),
          ),
        )
      }
      setRevokingId(null)
    },
    [http, load, t],
  )

  const expiryOptions = API_TOKEN_EXPIRY_CHOICES.map((days) => ({
    value: String(days),
    label: t('apiTokens.expiryDays', { days }, { defaultValue: '{{days}} days' }),
  }))

  return (
    <div data-mol-id="api-tokens" className={cm.stack(3)}>
      <p className={cm.cn(cm.textSize('sm'), cm.textMuted)} data-mol-id="api-tokens-intro">
        {t('apiTokens.intro', undefined, {
          defaultValue:
            'For scripts, CI and the CLI. A token acts as your account, except for security actions such as changing your password or email, managing tokens or deleting the account.',
        })}{' '}
        <Link
          to="/docs/api-tokens"
          data-mol-id="api-tokens-docs-link"
          className={cm.cn(cm.textPrimary, cm.fontWeight('semibold'))}
        >
          {t('apiTokens.docsLink', undefined, { defaultValue: 'How tokens work' })}
        </Link>
      </p>

      {revealed && <TokenReveal created={revealed} onDismiss={() => setRevealed(null)} />}

      {loading ? (
        <Flex align="center" justify="center" className={cm.sp('py', 3)}>
          <MoleculeSpinner size="xs" />
        </Flex>
      ) : loadError ? (
        <Flex align="center" gap="sm" style={{ flexWrap: 'wrap' }}>
          <span
            className={cm.cn(cm.textSize('sm'), cm.textError)}
            data-mol-id="api-tokens-load-error"
          >
            {loadError}
          </span>
          <Button
            size="sm"
            variant="outline"
            className={cm.touchTargetCompact}
            data-mol-id="api-tokens-retry"
            onClick={() => {
              setLoading(true)
              void load()
            }}
          >
            {t('common.retry', undefined, { defaultValue: 'Retry' })}
          </Button>
        </Flex>
      ) : tokens && tokens.length > 0 ? (
        <ul className={cm.stack(2)} data-mol-id="api-tokens-list">
          {tokens.map((token) => {
            const expired = isExpired(token)
            return (
              <li
                key={token.id}
                data-mol-id={`api-tokens-row-${token.id}`}
                style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: 8 }}
              >
                <Flex align="center" justify="between" gap="sm">
                  <div style={{ minWidth: 0 }}>
                    <div
                      className={cm.cn(cm.textSize('sm'), cm.fontWeight('semibold'))}
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      {token.name}
                    </div>
                    <div className={cm.cn(cm.textSize('xs'), cm.textMuted)}>
                      {[
                        t(
                          'apiTokens.created',
                          { date: shortDate(token.createdAt) },
                          { defaultValue: 'Created {{date}}' },
                        ),
                        expired
                          ? t(
                              'apiTokens.expired',
                              { date: shortDate(token.expiresAt) },
                              { defaultValue: 'Expired {{date}}' },
                            )
                          : t(
                              'apiTokens.expires',
                              { date: shortDate(token.expiresAt) },
                              { defaultValue: 'Expires {{date}}' },
                            ),
                        token.lastUsedAt
                          ? t(
                              'apiTokens.lastUsed',
                              { date: shortDate(token.lastUsedAt) },
                              { defaultValue: 'Last used {{date}}' },
                            )
                          : t('apiTokens.neverUsed', undefined, { defaultValue: 'Never used' }),
                      ].join(' · ')}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    color="error"
                    className={cm.touchTargetCompact}
                    data-mol-id={`api-tokens-revoke-${token.id}`}
                    disabled={revokingId === token.id}
                    onClick={() => void revoke(token)}
                  >
                    {t('apiTokens.revoke', undefined, { defaultValue: 'Revoke' })}
                  </Button>
                </Flex>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className={cm.cn(cm.textSize('sm'), cm.textMuted)} data-mol-id="api-tokens-empty">
          {t('apiTokens.empty', undefined, { defaultValue: 'No tokens yet.' })}
        </p>
      )}

      {revokeError && (
        <p className={cm.cn(cm.textSize('xs'), cm.textError)} data-mol-id="api-tokens-revoke-error">
          {revokeError}
        </p>
      )}

      {/* Create — label above each full-width field, the panel's form idiom. */}
      <form
        data-mol-id="api-tokens-create"
        className={cm.stack(2)}
        onSubmit={(e) => {
          e.preventDefault()
          void create()
        }}
      >
        <div>
          <label htmlFor="api-tokens-name" className={cm.formLabelSmall}>
            {t('apiTokens.nameLabel', undefined, { defaultValue: 'Token name' })}
          </label>
          <Input
            id="api-tokens-name"
            data-mol-id="api-tokens-name"
            value={name}
            maxLength={API_TOKEN_NAME_MAX}
            size="sm"
            align="left"
            placeholder={t('apiTokens.namePlaceholder', undefined, {
              defaultValue: 'GitHub Actions',
            })}
            onChange={(e) => setName((e.target as HTMLInputElement).value)}
            disabled={creating}
            style={isMobile ? { fontSize: 16 } : undefined}
          />
        </div>
        <div>
          <label htmlFor="api-tokens-expiry" className={cm.formLabelSmall}>
            {t('apiTokens.expiryLabel', undefined, { defaultValue: 'Expires in' })}
          </label>
          <Select
            id="api-tokens-expiry"
            data-mol-id="api-tokens-expiry"
            size="sm"
            value={expiry}
            onValueChange={setExpiry}
            options={expiryOptions}
            disabled={creating}
            style={isMobile ? { fontSize: 16 } : undefined}
          />
        </div>
        {atLimit && (
          <p className={cm.cn(cm.textSize('xs'), cm.textMuted)} data-mol-id="api-tokens-limit">
            {t(
              'apiTokens.limitReached',
              { max: MAX_ACTIVE_API_TOKENS },
              { defaultValue: 'You have {{max}} active tokens. Revoke one to create another.' },
            )}
          </p>
        )}
        <div>
          <Button
            type="submit"
            size="sm"
            className={cm.touchTargetCompact}
            data-mol-id="api-tokens-create-submit"
            loading={creating}
            disabled={creating || !trimmedName || atLimit}
          >
            {t('apiTokens.create', undefined, { defaultValue: 'Create token' })}
          </Button>
        </div>
        {createError && (
          <p
            className={cm.cn(cm.textSize('xs'), cm.textError)}
            data-mol-id="api-tokens-create-error"
          >
            {createError}
          </p>
        )}
      </form>
    </div>
  )
}
